import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  Button,
  Collapse,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DocumentHeader from './DocumentHeader';
import DocumentSection from './DocumentSection';
import TableOfContents from './TableOfContents';

const LegalDocument = ({ document }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showTOC, setShowTOC] = useState(!isMobile);

  const handlePrint = () => {
    window.print();
  };

  const scrollToSection = (sectionId) => {
    const element = window.document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        {/* Document Header */}
        <DocumentHeader 
          title={document.title}
          lastUpdated={document.lastUpdated}
          effectiveDate={document.effectiveDate}
          onPrint={handlePrint}
        />

        <Divider sx={{ my: 3 }} />

        {/* Table of Contents Toggle (Mobile) */}
        {isMobile && (
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setShowTOC(!showTOC)}
              endIcon={showTOC ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              fullWidth
            >
              {showTOC ? 'Hide' : 'Show'} Table of Contents
            </Button>
          </Box>
        )}

        {/* Layout Container */}
        <Box sx={{ display: 'flex', gap: 4 }}>
          {/* Table of Contents */}
          <Collapse in={showTOC} orientation={isMobile ? 'vertical' : 'horizontal'}>
            <Box sx={{ 
              width: isMobile ? '100%' : '250px', 
              flexShrink: 0,
              mb: isMobile ? 3 : 0 
            }}>
              <TableOfContents 
                sections={document.sections}
                onSectionClick={scrollToSection}
              />
            </Box>
          </Collapse>

          {/* Document Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Introduction */}
            {document.introduction && (
              <Box sx={{ mb: 4, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  {document.introduction}
                </Typography>
              </Box>
            )}

            {/* Important Notice */}
            {document.importantNotice && (
              <Box sx={{ mb: 4, p: 3, backgroundColor: '#fff3e0', border: '2px solid #ff9800', borderRadius: 2 }}>
                <Typography variant="h6" color="warning.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                  IMPORTANT NOTICE
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  {document.importantNotice}
                </Typography>
              </Box>
            )}

            {/* Document Sections */}
            {document.sections.map((section, index) => (
              <DocumentSection 
                key={section.id}
                section={section}
                index={index + 1}
              />
            ))}

            {/* Footer */}
            <Divider sx={{ my: 4 }} />
            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">
                Last Updated: {document.lastUpdated}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                © {new Date().getFullYear()} Inked Up Manager. All rights reserved.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          .MuiContainer-root {
            max-width: none !important;
            padding: 0 !important;
          }
          .MuiPaper-root {
            box-shadow: none !important;
            padding: 20px !important;
          }
          .print-hide {
            display: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default LegalDocument;